import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule, NgForm } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { switchMap } from 'rxjs';
import { OrganizationService } from '../../../services/organization.service'; // Assume this service exists
import { TextToSpeechService } from '../../../services/text-to-speech.service';
import { OrganizationDto } from '../../../models/organization-dto';
import { SmartAppEnvironmentDto } from '../../../models/smartApp-environment-dto';
import { DataExportVersionDto } from '../../../models/dataExport-version-dto';
import { PaginatedResponse } from '../../../models/paginated-response';
import { NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { ReadOnlyIfNoGroupPermissionDirectiveDirective } from '../../../directives/read-only-if-no-group-permission-directive.directive';

interface WelcomeMessage {
  language: string;
  message: string;
}

@Component({
  selector: 'app-organization-form',
  standalone: true,
  imports: [FormsModule, CommonModule, NgbNavModule, ReadOnlyIfNoGroupPermissionDirectiveDirective],
  templateUrl: './organization-form.component.html',
  styleUrls: ['./organization-form.component.scss'],
})
export class OrganizationFormComponent implements OnInit {
saveOrganization() {
throw new Error('Method not implemented.');
}
  @ViewChild('audioPlayer', { static: true }) audioPlayer!: ElementRef<HTMLAudioElement>;
  @ViewChild('orgForm') orgForm!: NgForm;

  organization: OrganizationDto = {
    id: '',
    name: '',
    isDepartmentEntryComplete: false,
    smartAppEnvironments: [],
    exportHistory: [],
  };

  // smart app environments

  environmentForms: {
    [envId: string]: {
      smartPhoneWelcomeMessages: WelcomeMessage[],
      smartPhoneFirstPrompts: WelcomeMessage[],
      smartChatWelcomeMessages: WelcomeMessage[],
      selectedDatasetType: string,
      exportVersion: string,
      exportNotes: string
    }
  } = {};

  exportHistory: DataExportVersionDto[] = [];
  currentPage: number = 1;
  pageSize: number = 10;
  totalPages: number = 0;
  totalCount: number = 0;
  hasNextPage: boolean = false;
  hasPreviousPage: boolean = false;
  datasetTypes: string[] = ['Department', 'Knowledge Base'];
  activeTab: string = '';

  constructor(
    private organizationService: OrganizationService,
    private route: ActivatedRoute,
    private router: Router,
    private textToSpeechService: TextToSpeechService
  ) { }

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        switchMap((params) => {
          return this.organizationService.getOrganization();
        })
      )
      .subscribe({
        next: (org) => {
          this.organization = { ...org };
          this.loadExportHistory();
          this.initializeEnvironmentForms();
          this.activeTab = this.organization.smartAppEnvironments.find(e => e.isDefault)?.id 
          || this.organization.smartAppEnvironments[0]?.id
          ||  '';       
        },
        error: (error) => console.error('Error loading organization:', error),
      });
  }

  cancel(): void {
    this.router.navigate(['/organizations']);
  }

  playText(message?: string): void {
    if (!message) {
      alert('Please enter a message to play.');
      return;
    }
    this.textToSpeechService.convertTextToSpeech(message).subscribe((audioBlob) => {
      const audioUrl = URL.createObjectURL(audioBlob);
      this.audioPlayer.nativeElement.src = audioUrl;
      this.audioPlayer.nativeElement.play();
    });
  }


  //==============================================
  // Smart App Environment Methods

  initializeEnvironmentForms(): void {
    this.organization.smartAppEnvironments.forEach(env => {
      console.log('Initializing environment form for:', env);
      this.environmentForms[env.id] = {
        smartPhoneWelcomeMessages: this.parseMessageString(env.smartPhoneWelcomeMessage),
        smartPhoneFirstPrompts: this.parseMessageString(env.smartPhoneFirstPrompt),
        smartChatWelcomeMessages: this.parseMessageString(env.smartChatWelcomeMessage),
        selectedDatasetType: 'Department',
        exportVersion: '1',
        exportNotes: ''
      };
    });
  }

  saveEnvironment(environmentId: string, form: NgForm): void {
    if (form.invalid) {
      alert('Please fill in all required fields.');
      return;
    }

    const envData = this.environmentForms[environmentId];
    const envUpdate: Partial<SmartAppEnvironmentDto> = {
      id: environmentId,
      smartPhoneWelcomeMessage: this.stringifyWelcomeMessages(envData.smartPhoneWelcomeMessages),
      smartPhoneFirstPrompt: this.stringifyWelcomeMessages(envData.smartPhoneFirstPrompts),
      smartChatWelcomeMessage: this.stringifyWelcomeMessages(envData.smartChatWelcomeMessages)
    };

    this.organizationService.updateExternalEnvironment(environmentId, envUpdate)
      .subscribe({
        next: () => alert('Environment messages saved successfully.'),
        error: (error) => console.error('Error saving environment:', error)
      });
  }

  promoteData(environmentId: string): void {
    const envData = this.environmentForms[environmentId];
    if (!this.organization.id) {
      alert('Organization ID is missing.');
      return;
    }
    if (!envData.selectedDatasetType) {
      alert('Please select a dataset type.');
      return;
    }
    if (!envData.exportVersion) {
      alert('Please enter a version.');
      return;
    }

    this.organizationService.promoteData(
      this.organization.id,      
      environmentId,
      envData.selectedDatasetType,      
      envData.exportNotes
    ).subscribe({
      next: () => {
        alert('Data export initiated successfully.');
        this.loadExportHistory();
        envData.exportVersion = (parseInt(envData.exportVersion) + 1).toString();
        envData.exportNotes = '';
      },
      error: (error) => console.error('Error promoting data:', error)
    });
  }

  loadExportHistory(): void {
    if (this.organization.id) {
      this.organizationService.getExportHistory(undefined, this.currentPage, this.pageSize)
        .subscribe({
          next: (response) => {
            this.exportHistory = response.items;
            this.totalPages = response.totalPages;
            this.totalCount = response.totalCount;
            this.hasNextPage = response.hasNextPage;
            this.hasPreviousPage = response.hasPreviousPage;
          },
          error: (error) => console.error('Error loading export history:', error)
        });
    }
  }

  changePage(page: number): void {
    this.currentPage = page;
    this.loadExportHistory();
  }

  //==============================================
  // Smart Phone and Smart Chat Messages

  parseMessageString(messageString?: string): WelcomeMessage[] {
    if (!messageString) return [];
    return messageString.split(';').map((pair) => {
      const [language, message] = pair.split(':');
      return { language: language || '', message: message || '' };
    }).filter(msg => msg.language && msg.message);
  }

  // Convert welcome message arrays back to semicolon-separated strings
  stringifyWelcomeMessages(messages: WelcomeMessage[]): string | undefined {
    if (!messages || messages.length === 0) return undefined;
    return messages.map((msg) => `${msg.language}:${msg.message}`).join(';');
  }


  addWelcomeMessage(messages: WelcomeMessage[]): void {
    messages.push({ language: '', message: '' });
  }

  removeWelcomeMessage(messages: WelcomeMessage[], index: number): void {
    messages.splice(index, 1);
  }
}

